import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API } from "../../lib/api";
import EsitoBadge from "../../components/EsitoBadge";

/* ==========================================================================
   Fascicolo — SCHERMATA DI SOLA LETTURA (Admin)
   ==========================================================================
   L'integrità del Protocollo esige che ogni modifica ai dati dell'immobile
   (anagrafica, catasto, esito, 10 aree del Fascicolo, RIUC, foto, pubblicazione)
   passi ESCLUSIVAMENTE dal modal "Modifica" della lista Immobili — mai da
   auto-save silenzioso su questa vista. Qui si consulta e si stampa (PDF).
   ========================================================================== */

const STATUS_LABEL = { da_fare: "Da fare", in_corso: "In corso", completata: "Completata" };
const RESPONSO_LABEL = {
  in_ordine: "✓ In ordine",
  criticita_dichiarata: "» Criticità dichiarata",
  vizio_trasferibilita: "✗ Vizio sulla trasferibilità",
  non_applicabile: "– Non applicabile",
};

const fmtMoney = (n) => (n == null || n === "" ? "—" : `€ ${Number(n).toLocaleString("it-IT")}`);
const fmtDateIT = (iso) => {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10).split("-");
  return s.length === 3 ? `${s[2]}/${s[1]}/${s[0]}` : iso;
};

const ReadRow = ({ label, value, testid, mono }) => (
  <div data-testid={testid}>
    <dt className="label-uppercase text-[#8A8580] text-[10px]">{label}</dt>
    <dd className={`mt-1 text-sm text-[#1A1617] ${mono ? "font-mono" : ""}`}>{value ?? "—"}</dd>
  </div>
);

export default function PropertyFascicolo() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [technicians, setTechnicians] = useState([]);

  useEffect(() => {
    api.get(`/properties/${id}`).then((r) => setP(r.data));
    api.get("/technicians").then((r) => setTechnicians(r.data)).catch(() => {});
  }, [id]);

  if (!p) return <div className="p-8 text-[#5C5657]">Caricamento…</div>;

  const token = localStorage.getItem("rxcasa_token");
  const pdfUrl = (kind) => `${API}/properties/${id}/pdf/${kind}?auth=${token}`;
  const editHref = `/admin/immobili?edit=${id}`;
  const tecnicoNome = (tid) => technicians.find((t) => t.id === tid)?.name || null;

  const catastoStr = [
    p.catasto_foglio && `F ${p.catasto_foglio}`,
    p.catasto_particella && `P ${p.catasto_particella}`,
    p.catasto_subalterno && `Sub ${p.catasto_subalterno}`,
    p.catasto_categoria && `Cat ${p.catasto_categoria}`,
  ].filter(Boolean).join(" · ") || null;

  const pertinenzeStr = [
    p.cantina && "Cantina", p.box_auto && "Box auto",
    p.terrazzo && "Terrazzo", p.balcone && "Balcone",
  ].filter(Boolean).join(", ") || null;

  return (
    <div data-testid="fascicolo-page">
      <Link to="/admin/immobili" className="text-xs text-[#5C5657] hover:text-[#7D2E3F]">← Immobili</Link>

      <div className="mt-4 flex justify-between items-start gap-4">
        <div>
          <div className="label-uppercase">Fascicolo · sola consultazione</div>
          <h1 className="font-serif text-3xl mt-1" data-testid="fascicolo-titolo">{p.titolo}</h1>
          <div className="text-sm text-[#5C5657] mt-1">{p.indirizzo} — {p.comune} ({p.provincia})</div>
          {p.fascicolo_code && (
            <div className="mt-2 inline-flex items-center gap-2 border border-[#7D2E3F] text-[#7D2E3F] px-2 py-1 font-mono text-xs tracking-wider" data-testid="fascicolo-code-admin">
              Fascicolo n° {p.fascicolo_code}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-3">
          <EsitoBadge esito={p.esito} testId="fascicolo-esito" />
          {p.published && <span className="badge-stamp badge-pronto">In Galleria</span>}
          {p.stato_pubblicazione === "venduto" ? (
            <span className="text-xs label-uppercase px-2 py-1 border border-[#5C5657] text-[#5C5657] bg-[#F4F3F1]" data-testid="status-badge" data-stato="venduto">
              Venduto{p.data_vendita ? ` · ${fmtDateIT(p.data_vendita)}` : ""}
            </span>
          ) : p.stato_pubblicazione === "in_vendita" ? (
            <span className="text-xs label-uppercase px-2 py-1 border border-[#245A2E] text-[#245A2E] bg-[#F0F7F1]" data-testid="status-badge" data-stato="in_vendita">Disponibile</span>
          ) : null}
        </div>
      </div>

      {/* Banner sola consultazione + Bottone Modifica prominente */}
      <div className="mt-6 border border-[#7D2E3F] bg-[#FDFDFC] p-5 flex items-center justify-between gap-4" data-testid="readonly-banner">
        <div className="text-sm text-[#3A3D42] leading-relaxed max-w-3xl">
          <strong>Vista in sola consultazione.</strong> Per modificare anagrafica, catasto, esito, le 10 aree
          del Fascicolo, foto, pubblicazione o stato commerciale, apri la scheda di modifica dell&apos;immobile.
        </div>
        <Link to={editHref} data-testid="open-edit-modal" className="btn-primary whitespace-nowrap">Modifica immobile</Link>
      </div>

      {/* Azioni non mutative: PDF */}
      <div className="admin-card p-5 mt-6 flex flex-wrap gap-3 items-center" data-testid="pdf-actions">
        <span className="label-uppercase mr-2">Documenti PDF:</span>
        <a href={pdfUrl("sintesi")} target="_blank" rel="noreferrer" data-testid="pdf-sintesi" className="btn-secondary text-xs">PDF Sintesi Criticità</a>
        <a href={pdfUrl("piu-valore")} target="_blank" rel="noreferrer" data-testid="pdf-piu-valore" className="btn-secondary text-xs">PDF Più Valore</a>
        <a href={pdfUrl("fascicolo")} target="_blank" rel="noreferrer" data-testid="pdf-fascicolo" className="btn-secondary text-xs">PDF Fascicolo</a>
      </div>

      {/* Anagrafica read-only */}
      <div className="mt-6 border border-[#E6E4E0] bg-white p-5" data-testid="anagrafica-summary">
        <div className="label-uppercase text-[#5C5657] mb-4">Dati immobile</div>
        <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
          <ReadRow label="Tipologia" value={p.tipologia} testid="ro-tipologia" />
          <ReadRow label="Superficie" value={p.superficie ? `${p.superficie} m²` : null} testid="ro-superficie" />
          <ReadRow label="Locali" value={p.locali || null} testid="ro-locali" />
          <ReadRow label="Camere" value={p.camere || null} testid="ro-camere" />
          <ReadRow label="Bagni" value={p.bagni || null} testid="ro-bagni" />
          <ReadRow label="Piano" value={p.piano || null} testid="ro-piano" />
          <ReadRow label="Anno" value={p.anno || null} testid="ro-anno" />
          <ReadRow label="Classe energetica" value={p.classe_energetica || null} testid="ro-ape" />
          <ReadRow label="Riscaldamento" value={p.riscaldamento || null} testid="ro-riscaldamento" />
          <ReadRow label="Stato manutentivo" value={p.stato_manutentivo || null} testid="ro-manutentivo" />
          <ReadRow label="Ascensore" value={p.ascensore ? "Sì" : null} testid="ro-ascensore" />
          <ReadRow label="Arredato" value={p.arredato || null} testid="ro-arredato" />
          <ReadRow label="Prezzo richiesto" value={fmtMoney(p.prezzo)} testid="ro-prezzo" />
          <ReadRow label="Spese condominiali/anno" value={fmtMoney(p.spese_condominiali_annue)} testid="ro-spese" />
          <ReadRow label="Pertinenze" value={pertinenzeStr} testid="ro-pertinenze" />
          <ReadRow label="Catasto" value={catastoStr} testid="ro-catasto" mono />
          <ReadRow label="Pertinenze catastali" value={(p.catasto_pertinenze || []).length ? `${p.catasto_pertinenze.length} unità` : null} testid="ro-cat-pertinenze" />
          <ReadRow label="Agenzia" value={p.agency?.name || null} testid="ro-agency" />
          <ReadRow label="Agente" value={p.agente_nome || null} testid="ro-agent" />
          <ReadRow label="Venditore" value={p.seller ? `${p.seller.cognome} ${p.seller.nome}` : null} testid="ro-seller" />
          <div className="col-span-2 md:col-span-3">
            <ReadRow label="Indirizzo" value={[p.indirizzo, p.comune && `${p.comune}${p.provincia ? ` (${p.provincia})` : ""}`, p.cap].filter(Boolean).join(" · ") || null} testid="ro-indirizzo" />
          </div>
          {p.descrizione && (
            <div className="col-span-2 md:col-span-3">
              <ReadRow label="Descrizione" value={p.descrizione} testid="ro-descrizione" />
            </div>
          )}
        </dl>
      </div>

      {/* Foto read-only */}
      <div className="admin-card p-5 mt-6" data-testid="photos-section">
        <div className="flex justify-between items-center mb-4">
          <div><div className="label-uppercase">Fotografia</div><h3 className="font-serif text-xl">{p.photos?.length || 0} foto</h3></div>
          <Link to={editHref} className="text-xs label-uppercase text-[#7D2E3F] hover:underline">Gestisci foto in Modifica →</Link>
        </div>
        {(p.photos || []).length === 0 ? (
          <div className="text-sm text-[#5C5657]">Nessuna foto caricata.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            {p.photos.map((ph, idx) => {
              const src = ph.external_url || `${API}/files/${ph.path}?auth=${token}`;
              return (
                <div key={ph.id || idx} className="relative aspect-square bg-[#F4F3F1] border border-[#E6E4E0] overflow-hidden" data-testid={`photo-tile-${ph.id || idx}`}>
                  <img src={src} alt="" className="w-full h-full object-cover" />
                  {idx === 0 && <div className="absolute top-1 left-1 z-10 text-[10px] label-uppercase bg-[#7D2E3F] text-white px-2 py-0.5">Copertina</div>}
                  <div className="absolute top-1 right-1 z-10 text-[10px] bg-white/85 text-[#5C5657] px-2 py-0.5 font-medium">{idx + 1}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Esito Globale read-only */}
      <div className="admin-card p-5 mt-6" data-testid="ro-esito-block">
        <div className="label-uppercase text-[#5C5657] mb-3">Esito globale del Protocollo</div>
        <div className="flex items-center gap-3">
          <EsitoBadge esito={p.esito} />
          {!p.esito && <span className="text-sm text-[#5C5657]">Non ancora attribuito</span>}
          <div className="flex-1" />
          <Link to={editHref} className="text-xs label-uppercase text-[#7D2E3F] hover:underline">Attribuisci/modifica in scheda →</Link>
        </div>
      </div>

      {/* 10 Aree di verifica — READ ONLY */}
      <div className="mt-6" data-testid="verifiche-section">
        <div className="flex justify-between items-baseline mb-4">
          <h2 className="font-serif text-2xl">Le 10 aree di verifica</h2>
          <Link to={editHref} className="text-xs label-uppercase text-[#7D2E3F] hover:underline">Modifica in scheda →</Link>
        </div>
        <div className="space-y-3">
          {(p.verifiche || []).map((v, i) => (
            <div key={v.key} data-testid={`area-${v.key}`} className="admin-card p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="label-uppercase">Area {String(i + 1).padStart(2, "0")}</div>
                  <h3 className="font-serif text-xl mt-1">{v.label}</h3>
                </div>
                <span className="text-xs label-uppercase px-2 py-1 border border-[#E6E4E0] text-[#5C5657]" data-testid={`ro-status-${v.key}`}>
                  {STATUS_LABEL[v.status] || "—"}
                </span>
              </div>
              <dl className="grid grid-cols-2 gap-4 mt-4">
                <ReadRow label="Responso" value={v.responso ? RESPONSO_LABEL[v.responso] : null} testid={`ro-responso-${v.key}`} />
                <ReadRow
                  label="Classificazione criticità"
                  value={v.responso === "criticita_dichiarata" ? (v.rilevante === true ? "Rilevante" : v.rilevante === false ? "Non Rilevante" : null) : null}
                  testid={`ro-rilevante-${v.key}`}
                />
              </dl>
              <dl className="grid grid-cols-2 gap-4 mt-3">
                <ReadRow label="Note interne (Schede di Verifica)" value={v.note || null} testid={`ro-note-${v.key}`} />
                <ReadRow label="Criticità dichiarata (pubblica)" value={v.criticita || null} testid={`ro-criticita-${v.key}`} />
              </dl>
              <dl className="grid grid-cols-3 gap-4 mt-3">
                <ReadRow label="Costo stimato" value={v.costo_stimato != null ? fmtMoney(v.costo_stimato) : null} testid={`ro-costo-${v.key}`} />
                <ReadRow label="Tempi" value={v.tempi || null} testid={`ro-tempi-${v.key}`} />
                <ReadRow label="A carico di" value={v.a_carico_di || null} testid={`ro-carico-${v.key}`} />
              </dl>
              <dl className="grid grid-cols-2 gap-4 mt-3">
                <ReadRow label="Tecnico" value={tecnicoNome(v.tecnico_id)} testid={`ro-tecnico-${v.key}`} />
                <div>
                  <dt className="label-uppercase text-[#8A8580] text-[10px]">Documenti</dt>
                  <dd className="mt-1 text-sm">
                    {(v.documents || []).length === 0 ? (
                      <span className="text-[#5C5657]">—</span>
                    ) : (
                      (v.documents || []).map((d) => (
                        <div key={d.id}>
                          <a href={`${API}/files/${d.path}?auth=${token}`} target="_blank" rel="noreferrer" className="text-[#7D2E3F] hover:underline">{d.filename}</a>
                        </div>
                      ))
                    )}
                  </dd>
                </div>
              </dl>
            </div>
          ))}
        </div>
      </div>

      {/* Footer: azione modifica in coda */}
      <div className="mt-8 mb-16 flex justify-end">
        <Link to={editHref} data-testid="open-edit-modal-bottom" className="btn-primary">Modifica immobile</Link>
      </div>
    </div>
  );
}
